/**
 * LeaderGettingStartedPage — public Leader launch guide (#2836)
 *
 * A public "Getting Started for Club Leaders" page: a launch guide (not a docs
 * index) that gets a new leader's next real club activity running through ORO
 * move. Faithful port of the approved draft
 * (docs/canonical/features/in-progress/leader-getting-started-page.draft.html).
 *
 * - Public route (no ProtectedRoute); deep-link CTAs point at protected routes,
 *   where the magic-link flow on arrival is expected behavior.
 * - Sticky 5-item launch checklist persisted to localStorage only (no backend).
 * - Two copy-to-clipboard message modules (event announcement, member invite).
 * - Layer 2 tokens + Lucide icons only. UX layer: Context.
 *
 * Feature doc: docs/canonical/features/in-progress/leader-getting-started-page.md
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import StaticFooter from '../components/StaticFooter';
import EnvironmentBanner from '../components/EnvironmentBanner';
import OroTrademark from '../components/OroTrademark';

// localStorage key — namespaced with the app's `oro-` convention.
const CHECKLIST_STORAGE_KEY = 'oro-leader-launch-checklist';

interface ChecklistItem {
  id: string;
  label: string;
}

const CHECKLIST: ChecklistItem[] = [
  { id: 'ck-1', label: 'Club looks like your club' },
  { id: 'ck-2', label: 'Another leader added' },
  { id: 'ck-3', label: 'First real event created' },
  { id: 'ck-4', label: 'Members invited' },
  { id: 'ck-5', label: 'First event run through ORO move' },
];

const WEEK_STEPS: { title: string; body: string }[] = [
  {
    title: 'Make your club feel like your club',
    body: "Add your club's description, location, photo, sport, and links so members know they've landed in the right place.",
  },
  {
    title: 'Bring in another leader',
    body: "If other people help run the club, add them early. ORO move works better when club knowledge and responsibilities don't live with one person.",
  },
  {
    title: 'Create your next event',
    body: "Don't make a test event. Add the run, ride, walk, meetup, or workout your group is actually doing next.",
  },
  {
    title: 'Invite your people',
    body: "Once there's something waiting for them, share your club invite through the places your members already communicate.",
  },
  {
    title: 'Run the event through ORO move',
    body: 'Let members RSVP, keep the event details current, and use check-in on event day when it makes sense for your club.',
  },
];

const SETTINGS_CELLS: { title: string; body: string }[] = [
  {
    title: 'Club settings',
    body: "Review your club's visibility, membership permissions, invitations, member roles, links, and other details.",
  },
  {
    title: 'Calendar',
    body: 'Give members an easier way to keep club events alongside everything else on their calendar.',
  },
  {
    title: 'Event defaults',
    body: 'If your events tend to share the same settings, configure sensible defaults rather than rebuilding the same event every week.',
  },
  {
    title: 'Members & roles',
    body: 'As your leadership team grows, give people the access they need without giving everyone the keys to the building.',
  },
];

// Exact clipboard payloads. The visible copy matches the draft verbatim; the
// multi-paragraph invite is joined with blank lines so it pastes cleanly into
// text/social channels.
const ANNOUNCE_MESSAGE =
  "We've put Saturday's run in ORO move. You'll find the location, details, and RSVP there. We're trying it as a simpler way to keep everything for the club together.";

const INVITE_MESSAGE = [
  "We're using ORO move to keep our club events and details together.",
  'Join us here: [your club link]',
  "You'll be able to see upcoming events, RSVP, get club updates, and have the important details in one place.",
].join('\n\n');

// Dark-on-gold CTA. These render as <Link> (<a>), and index.css has an
// UNLAYERED `a { color: inherit }` reset that outranks any layered Tailwind
// text-color utility (unlayered wins over @layer utilities regardless of
// specificity). The `!` important modifier forces the dark label so the text
// reads on the gold fill instead of inheriting near-white body color (#3058).
const primaryBtn =
  'inline-flex w-fit items-center gap-2 rounded-[10px] bg-accent px-[22px] py-[11px] text-[0.92rem] font-semibold text-oro-bg! transition-shadow hover:bg-accent-light hover:shadow-glow';

const ghostBtn =
  'inline-flex w-fit items-center gap-2 rounded-[10px] border border-oro-gold/35 px-[22px] py-[11px] text-[0.92rem] font-semibold text-oro-gold transition-colors hover:border-oro-gold hover:text-oro-gold-light';

const eyebrow =
  'text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-oro-gold';

type CopyStatus = 'idle' | 'copied' | 'failed';

/** Copy-to-clipboard button with a transient confirmation / failure state. */
function CopyButton({ text, label, testId }: { text: string; label: string; testId: string }) {
  const [status, setStatus] = useState<CopyStatus>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = useCallback(async () => {
    let next: CopyStatus;
    try {
      await navigator.clipboard.writeText(text);
      next = 'copied';
    } catch {
      // Clipboard blocked (permissions / insecure context) — surface a brief
      // failure hint so the click isn't silently swallowed (aria-live announces).
      next = 'failed';
    }
    setStatus(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setStatus('idle'), 1800);
  }, [text]);

  const copied = status === 'copied';
  const failed = status === 'failed';

  return (
    <button
      type="button"
      onClick={handleCopy}
      data-testid={testId}
      className={`inline-flex min-h-11 items-center gap-[7px] rounded-lg border px-[14px] py-[7px] text-[0.8rem] font-semibold transition-colors ${
        copied
          ? 'border-oro-success text-oro-success'
          : failed
            ? 'border-oro-error text-oro-error'
            : 'border-oro-gold/35 text-oro-gold hover:border-oro-gold hover:text-oro-gold-light'
      }`}
      aria-live="polite"
    >
      {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
      {copied ? 'Copied' : failed ? 'Copy failed' : label}
    </button>
  );
}

/** Read the persisted checklist state (client-only SPA — localStorage is safe). */
function loadChecklist(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(CHECKLIST_STORAGE_KEY);
    const saved: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(saved)) return {};
    const next: Record<string, boolean> = {};
    saved.forEach((id) => {
      if (typeof id === 'string') next[id] = true;
    });
    return next;
  } catch {
    // Invalid JSON / private mode — start from an empty checklist.
    return {};
  }
}

export default function LeaderGettingStartedPage() {
  const [checked, setChecked] = useState<Record<string, boolean>>(loadChecklist);

  const toggle = useCallback((id: string) => {
    setChecked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        const ids = CHECKLIST.filter((item) => next[item.id]).map((item) => item.id);
        localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(ids));
      } catch {
        // localStorage full / disabled — degrade gracefully.
      }
      return next;
    });
  }, []);

  const doneCount = CHECKLIST.filter((item) => checked[item.id]).length;
  const progressPct = (doneCount / CHECKLIST.length) * 100;

  return (
    <div className="min-h-screen bg-oro-bg text-text-secondary">
      <EnvironmentBanner />
      <PublicHeader />

      <main>
        {/* ================= HERO ================= */}
        <header className="mx-auto max-w-[1140px] px-6">
          <div className="flex max-w-[680px] flex-col gap-[22px] pb-[72px] pt-[64px]">
            <span className={eyebrow}>Getting started for club leaders</span>
            <h1 className="text-[clamp(2.1rem,5.2vw,3.2rem)] font-bold leading-[1.2] tracking-[-0.02em] text-text-primary text-balance">
              Welcome to <OroTrademark />
            </h1>
            <p className="text-[clamp(1.15rem,2.4vw,1.45rem)] font-medium tracking-[-0.01em] text-oro-gold">
              Less time coordinating. More time moving together.
            </p>
            <p className="max-w-[58ch]">
              You already know how to lead your club. ORO move is here to make the logistics
              around it a little easier — keeping your club, events, RSVPs, attendance, and
              conversations together so your people know what's happening and you spend less time
              piecing it all together.
            </p>
            <p className="max-w-[58ch]">
              You don't need to configure everything before you begin.{' '}
              <strong className="text-text-primary">Start with your next real event.</strong> Get
              your club ready, bring in another leader if you have one, create the event, and then
              invite your people.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/home" className={primaryBtn}>
                Open ORO move
              </Link>
            </div>
          </div>
        </header>

        {/* ================= BODY ================= */}
        <div className="mx-auto max-w-[1140px] px-6">
          <div className="grid grid-cols-1 gap-12 pb-10 min-[980px]:grid-cols-[minmax(0,1fr)_292px] min-[980px]:gap-16">
            {/* Sticky checklist rail — above content on mobile, right rail ≥980px */}
            <aside
              aria-label="Launch checklist"
              className="order-first min-[980px]:order-last"
            >
              <div className="sticky top-6 flex flex-col gap-4 rounded-[14px] border border-oro-border bg-oro-surface p-[22px] shadow-card">
                <div>
                  <span className={eyebrow}>Your first club week</span>
                  <h2 className="text-base text-text-primary">Launch checklist</h2>
                </div>
                <div>
                  <div className="h-[6px] overflow-hidden rounded-full bg-oro-surface-alt">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-oro-gold-dark to-oro-gold transition-[width] duration-300"
                      style={{ width: `${progressPct}%` }}
                      data-testid="progress-fill"
                    />
                  </div>
                  <p className="mt-2 text-[0.75rem] tabular-nums text-text-muted" data-testid="progress-label">
                    {doneCount} of {CHECKLIST.length} done
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  {CHECKLIST.map((item) => {
                    const isChecked = !!checked[item.id];
                    return (
                      <label
                        key={item.id}
                        htmlFor={item.id}
                        className="flex min-h-11 flex-1 cursor-pointer items-center gap-3 rounded-[10px] px-[10px] py-[9px] text-[0.9rem] text-text-secondary transition-colors hover:bg-oro-surface-alt"
                      >
                        <input
                          id={item.id}
                          type="checkbox"
                          className="peer sr-only"
                          checked={isChecked}
                          onChange={() => toggle(item.id)}
                        />
                        <span
                          className={`grid size-5 flex-none place-items-center rounded-md border-[1.5px] transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-oro-gold ${
                            isChecked
                              ? 'border-oro-gold bg-oro-gold'
                              : 'border-text-primary/30'
                          }`}
                          aria-hidden="true"
                        >
                          {isChecked && <Check size={12} strokeWidth={3.4} className="text-oro-bg" />}
                        </span>
                        <span className={isChecked ? 'text-text-muted' : undefined}>{item.label}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[0.78rem] text-text-muted">
                  That's enough to get started. Everything else can come later.
                </p>
              </div>
            </aside>

            {/* Main flow */}
            <div className="flex flex-col">
              {/* Week plan — the spine */}
              <section className="pb-14 pt-3" id="first-week">
                <h2 className="text-[clamp(1.35rem,2.6vw,1.7rem)] font-semibold tracking-[-0.015em] text-text-primary text-balance">
                  Your first club week
                </h2>
                <div className="mt-5 flex max-w-[62ch] flex-col gap-4">
                  <p>You can get the important pieces in place pretty quickly.</p>
                </div>
                <div className="mt-6 flex max-w-[62ch] flex-col gap-[22px]">
                  {WEEK_STEPS.map((step, i) => (
                    <div key={step.title} className="flex gap-[18px]">
                      <span className="mt-0.5 grid size-[30px] flex-none place-items-center rounded-full border border-oro-gold/40 text-[0.8rem] font-bold tabular-nums text-oro-gold">
                        {i + 1}
                      </span>
                      <div>
                        <h3 className="mb-1 text-base font-semibold text-text-primary">{step.title}</h3>
                        <p className="text-[0.95rem]">{step.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* 01 — Make the club yours */}
              <section className="border-t border-oro-border py-14" id="make-it-yours">
                <div className="mb-5 flex items-baseline gap-4">
                  <span className="flex-none -translate-y-0.5 text-[0.8rem] font-bold tracking-[0.1em] tabular-nums text-oro-gold">
                    01
                  </span>
                  <h2 className="text-[clamp(1.35rem,2.6vw,1.7rem)] font-semibold tracking-[-0.015em] text-text-primary text-balance">
                    Make the club yours
                  </h2>
                </div>
                <div className="flex max-w-[62ch] flex-col gap-4">
                  <p>Before you invite everyone in, spend a few minutes making your club recognizable.</p>
                  <p>Start with the basics:</p>
                  <ul className="flex list-disc flex-col gap-[10px] pl-[22px] marker:text-oro-gold-dark">
                    <li>
                      <strong className="font-semibold text-text-primary">Club name and description</strong> — tell
                      people who you are without writing a manifesto.
                    </li>
                    <li>
                      <strong className="font-semibold text-text-primary">Location</strong> — help nearby people
                      understand where your community moves.
                    </li>
                    <li>
                      <strong className="font-semibold text-text-primary">Club photo and cover image</strong> —
                      familiar imagery makes the space immediately feel like yours.
                    </li>
                    <li>
                      <strong className="font-semibold text-text-primary">Primary activity</strong> — running,
                      cycling, walking, or however your group moves.
                    </li>
                    <li>
                      <strong className="font-semibold text-text-primary">Website and social links</strong> — ORO
                      move doesn't need to replace the parts of your community that already work.
                    </li>
                  </ul>
                  <p>
                    You can also choose how people find and join your club. Take a moment to review your club's
                    privacy and membership settings before sharing it widely.
                  </p>
                  <p>
                    <strong className="font-semibold text-text-primary">A good rule:</strong> configure for the club
                    you have today. You can change these settings later.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/home" className={primaryBtn}>
                      Open your club
                    </Link>
                    <Link to="/help/clubs/club-settings" className={ghostBtn}>
                      Club setup guide
                    </Link>
                  </div>
                </div>
              </section>

              {/* 02 — Leadership team */}
              <section className="border-t border-oro-border py-14" id="leadership">
                <div className="mb-5 flex items-baseline gap-4">
                  <span className="flex-none -translate-y-0.5 text-[0.8rem] font-bold tracking-[0.1em] tabular-nums text-oro-gold">
                    02
                  </span>
                  <h2 className="text-[clamp(1.35rem,2.6vw,1.7rem)] font-semibold tracking-[-0.015em] text-text-primary text-balance">
                    Bring in your leadership team
                  </h2>
                </div>
                <div className="flex max-w-[62ch] flex-col gap-4">
                  <p>Most healthy clubs aren't run by one person.</p>
                  <p>
                    If you have co-leaders, ride captains, run leads, board members, or other people who help
                    organize the group, bring at least one of them into ORO move before inviting the broader
                    membership.
                  </p>
                  <p>
                    Leaders can help keep events and club information current so everything doesn't depend on a
                    single person being available.
                  </p>
                  <p>You remain in control of your club and can manage member roles as the team changes.</p>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/home" className={primaryBtn}>
                      Invite a leader
                    </Link>
                    <Link to="/help/clubs/manage-members" className={ghostBtn}>
                      Learn about roles
                    </Link>
                  </div>
                </div>
              </section>

              {/* 03 — First real event */}
              <section className="border-t border-oro-border py-14" id="first-event">
                <div className="mb-5 flex items-baseline gap-4">
                  <span className="flex-none -translate-y-0.5 text-[0.8rem] font-bold tracking-[0.1em] tabular-nums text-oro-gold">
                    03
                  </span>
                  <h2 className="text-[clamp(1.35rem,2.6vw,1.7rem)] font-semibold tracking-[-0.015em] text-text-primary text-balance">
                    Create your first real event
                  </h2>
                </div>
                <div className="flex max-w-[62ch] flex-col gap-4">
                  <p>This is where ORO move starts becoming useful.</p>
                  <p>Choose the next event that's already on your club's calendar and put it into ORO move.</p>
                  <p>
                    Give members the information they'd normally have to hunt through texts, social posts, or chat
                    threads to find:
                  </p>
                  <ul className="flex list-disc flex-col gap-[10px] pl-[22px] marker:text-oro-gold-dark">
                    <li>What are we doing?</li>
                    <li>When are we meeting?</li>
                    <li>Where exactly should I be?</li>
                    <li>Is there a route or other information I should know?</li>
                    <li>Who else is planning to come?</li>
                  </ul>
                  <p>Add the details that matter to your group and leave out the ones that don't.</p>
                  <p>
                    Members can RSVP so leaders have a better idea of who's coming, and event information stays
                    attached to the event instead of disappearing into a conversation.
                  </p>
                  <p>When you're ready, share the event using ORO move or send people directly to your club.</p>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/events/create" className={primaryBtn}>
                      Create an event
                    </Link>
                    <Link to="/help/events/create-event" className={ghostBtn}>
                      Event creation guide
                    </Link>
                  </div>
                </div>

                <div className="mt-7 flex max-w-[62ch] flex-col gap-3 rounded-[14px] border border-oro-border bg-oro-surface px-6 py-[22px]">
                  <span className="text-[0.78rem] font-semibold uppercase tracking-[0.12em] text-oro-gold">
                    A small suggestion
                  </span>
                  <p>
                    Use the way your club already communicates to introduce the first ORO move event. You don't
                    need to announce a grand technology migration. Something simple works:
                  </p>
                  <blockquote className="border-l-2 border-oro-gold-dark pl-4 italic text-text-secondary">
                    {ANNOUNCE_MESSAGE}
                  </blockquote>
                  <div className="flex justify-end">
                    <CopyButton text={ANNOUNCE_MESSAGE} label="Copy message" testId="copy-announce" />
                  </div>
                  <p className="text-[0.88rem] text-text-muted">Let usefulness make the case.</p>
                </div>
              </section>

              {/* 04 — Invite your people */}
              <section className="border-t border-oro-border py-14" id="invite">
                <div className="mb-5 flex items-baseline gap-4">
                  <span className="flex-none -translate-y-0.5 text-[0.8rem] font-bold tracking-[0.1em] tabular-nums text-oro-gold">
                    04
                  </span>
                  <h2 className="text-[clamp(1.35rem,2.6vw,1.7rem)] font-semibold tracking-[-0.015em] text-text-primary text-balance">
                    Invite your people
                  </h2>
                </div>
                <div className="flex max-w-[62ch] flex-col gap-4">
                  <p>Once your club has an event waiting for them, invite the group.</p>
                  <p>
                    You can share your club through the channels your members already use — text, email, Instagram,
                    Facebook, Discord, or wherever your community already gathers.
                  </p>
                  <p>
                    <strong className="font-semibold text-text-primary">
                      ORO move doesn't need to replace your community's personality.
                    </strong>{' '}
                    The goal is to give everyone one dependable place for the operational stuff: where to be, when
                    to be there, what's happening, and what changed.
                  </p>
                  <p>
                    For a larger club, you may want to invite a small group first, make sure everything feels right,
                    and then share it more broadly.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/home" className={primaryBtn}>
                      Invite members
                    </Link>
                    <Link to="/help/clubs/invite-members" className={ghostBtn}>
                      How invites work
                    </Link>
                  </div>
                </div>

                <div className="mt-7 flex max-w-[62ch] flex-col gap-3 rounded-[14px] border border-oro-border bg-oro-surface px-6 py-[22px]">
                  <span className="text-[0.78rem] font-semibold uppercase tracking-[0.12em] text-oro-gold">
                    Copy this
                  </span>
                  <div className="rounded-[10px] border border-oro-border bg-oro-surface-alt px-[18px] py-4 text-[0.92rem]">
                    <p>
                      <strong className="font-semibold text-text-primary">
                        We're using ORO move to keep our club events and details together.
                      </strong>
                    </p>
                    <p className="mt-[10px]">
                      Join us here: <strong className="font-semibold text-text-primary">[your club link]</strong>
                    </p>
                    <p className="mt-[10px]">
                      You'll be able to see upcoming events, RSVP, get club updates, and have the important details
                      in one place.
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <CopyButton text={INVITE_MESSAGE} label="Copy invitation" testId="copy-invite" />
                  </div>
                </div>
              </section>

              {/* 05 — Event day */}
              <section className="border-t border-oro-border py-14" id="event-day">
                <div className="mb-5 flex items-baseline gap-4">
                  <span className="flex-none -translate-y-0.5 text-[0.8rem] font-bold tracking-[0.1em] tabular-nums text-oro-gold">
                    05
                  </span>
                  <h2 className="text-[clamp(1.35rem,2.6vw,1.7rem)] font-semibold tracking-[-0.015em] text-text-primary text-balance">
                    On event day
                  </h2>
                </div>
                <div className="flex max-w-[62ch] flex-col gap-4">
                  <p>ORO move should make event day calmer, not add another chore.</p>
                  <p>Before the event, you can see who's planning to attend and keep the event details current.</p>
                  <p>
                    When your group arrives, use check-in if attendance is useful to your club. Depending on how
                    you've configured the event, ORO move can support options such as location-based, QR, or
                    code-based check-in.
                  </p>
                  <p>
                    If plans change, keep the update attached to the event so people aren't searching through three
                    different places for the latest information.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/help/events/checkin-methods" className={ghostBtn}>
                      Learn about check-in
                    </Link>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>

        {/* Interstitial */}
        <div className="border-t border-oro-border py-[88px] text-center">
          <div className="mx-auto max-w-[1140px] px-6">
            <p className="text-[clamp(1.4rem,3.4vw,2.1rem)] font-medium tracking-[-0.015em] text-text-primary text-balance">
              Then go move.
            </p>
            <p className="mt-[14px] text-base text-text-muted">The software has done enough.</p>
          </div>
        </div>

        <div className="mx-auto max-w-[1140px] px-6">
          {/* After first event */}
          <section className="py-14" id="after-first-event">
            <h2 className="mb-5 text-[clamp(1.35rem,2.6vw,1.7rem)] font-semibold tracking-[-0.015em] text-text-primary text-balance">
              Once you've run your first event
            </h2>
            <p className="max-w-[62ch]">
              Now is a good time to explore the settings that can save you more work over time.
            </p>
            <div className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-4">
              {SETTINGS_CELLS.map((cell) => (
                <div key={cell.title} className="rounded-xl border border-oro-border bg-oro-surface px-5 py-[18px]">
                  <h3 className="mb-1.5 text-[0.95rem] font-semibold text-text-primary">{cell.title}</h3>
                  <p className="text-[0.88rem] text-text-muted">{cell.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 max-w-[62ch]">
              There are more tools available as your club grows, but none of them need to stand between you and
              getting started.
            </p>
          </section>

          {/* The ORO way */}
          <section className="py-14" id="the-oro-way">
            <h2 className="mb-5 text-[clamp(1.35rem,2.6vw,1.7rem)] font-semibold tracking-[-0.015em] text-text-primary text-balance">
              The ORO way
            </h2>
            <div className="flex max-w-[62ch] flex-col gap-4">
              <p>
                <strong className="font-semibold text-text-primary">
                  ORO move is built around the group, not the scoreboard.
                </strong>
              </p>
              <p>
                We think the best club software should help people{' '}
                <strong className="font-semibold text-text-primary">show up in the real world</strong>, then get out
                of the way.
              </p>
              <p>
                That means you don't need to turn every run into a performance metric, make every member compete on
                a leaderboard, or move every existing club conversation into a new app just because you can.
              </p>
              <p>Use what helps your community. Leave the rest alone.</p>
              <p>
                And if something in ORO move is making club leadership harder instead of easier, we'd genuinely like
                to know.
              </p>
            </div>
          </section>

          {/* Help */}
          <section className="border-t border-oro-border pb-24 pt-16" id="help">
            <div className="flex max-w-[62ch] flex-col gap-[18px]">
              <h2 className="text-[clamp(1.35rem,2.6vw,1.7rem)] font-semibold tracking-[-0.015em] text-text-primary text-balance">
                Need a hand?
              </h2>
              <p>
                We're building ORO move alongside real club leaders, and your questions help us see where the
                experience still needs work.
              </p>
              <p>
                <strong className="font-semibold text-text-primary">
                  Questions or something not behaving as expected?
                </strong>
                <br />
                <Link to="/contact" className="text-oro-gold hover:text-oro-gold-light hover:underline">
                  Contact support
                </Link>
              </p>
              <p>
                <strong className="font-semibold text-text-primary">
                  Have an idea that would make running your club easier?
                </strong>
                <br />
                <Link to="/feedback" className="text-oro-gold hover:text-oro-gold-light hover:underline">
                  Send us feedback
                </Link>
              </p>
              <p>Or just keep going:</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/home" className={primaryBtn}>
                  Open ORO move
                </Link>
              </div>
            </div>
          </section>

          <p className="border-t border-oro-border pb-12 pt-7 text-[0.78rem] text-text-muted">
            <OroTrademark /> · The app for communities that move together.
          </p>
        </div>
      </main>

      <StaticFooter />
    </div>
  );
}
